import {
    AUDIT_LOG_ACTION,
    AUDIT_LOG_CART_UPDATE_TYPE,
    type AuditLog,
    type AuditLogAddItemsAction,
    type AuditLogAddNoteAction,
    type AuditLogBase,
    type AuditLogEditedAction,
    type AuditLogRemoveItemsAction,
    type AuditLogUpdateModifiersAction,
    type AuditLogUpdateQuantitiesAction,
} from '../types/audit-log';

function isMergeable(log: AuditLog): boolean {
    return (
        log.action === AUDIT_LOG_ACTION.UPDATED ||
        log.action === AUDIT_LOG_ACTION.EDITED
    );
}

function getBucketKey(log: AuditLog): string {
    if (log.action === AUDIT_LOG_ACTION.UPDATED) {
        return `UPDATED:${log.cartUpdateType}:${log.staffId}`;
    }

    if (log.action === AUDIT_LOG_ACTION.EDITED) {
        return `EDITED:${log.changedFields[0]?.field ?? ''}:${log.staffId}`;
    }

    return '';
}

function withIdentity<T extends AuditLogBase>(
    base: T,
    source: AuditLogBase,
): T {
    return {
        ...base,
        id: source.id,
        timestamp: source.timestamp,
        authorizedById: source.authorizedById,
        authorizedByName: source.authorizedByName,
    };
}

function mergeArrays<T>(
    existing: T[],
    incoming: T[],
    keyOf: (item: T) => string,
    collapse: (prior: T, fresh: T) => T,
): T[] {
    const map = new Map<string, T>();
    existing.forEach((item) => {
        map.set(keyOf(item), item);
    });
    incoming.forEach((item) => {
        const key = keyOf(item);
        const prior = map.get(key);
        map.set(key, prior ? collapse(prior, item) : item);
    });
    return Array.from(map.values());
}

function mergeAddItems(
    last: AuditLogAddItemsAction,
    incoming: AuditLogAddItemsAction,
): AuditLogAddItemsAction {
    const itemsAdded = mergeArrays(
        last.itemsAdded,
        incoming.itemsAdded,
        (i) => i.variantId,
        (_, fresh) => fresh,
    );
    return withIdentity({ ...last, itemsAdded }, incoming);
}

function mergeRemoveItems(
    last: AuditLogRemoveItemsAction,
    incoming: AuditLogRemoveItemsAction,
): AuditLogRemoveItemsAction {
    const itemsRemoved = mergeArrays(
        last.itemsRemoved,
        incoming.itemsRemoved,
        (i) => i.variantId,
        (_, fresh) => fresh,
    );
    return withIdentity({ ...last, itemsRemoved }, incoming);
}

function mergeUpdateQuantities(
    last: AuditLogUpdateQuantitiesAction,
    incoming: AuditLogUpdateQuantitiesAction,
): AuditLogUpdateQuantitiesAction | null {
    const quantitiesChanged = mergeArrays(
        last.quantitiesChanged,
        incoming.quantitiesChanged,
        (q) => q.variantId,
        (prior, fresh) => ({ ...fresh, oldQty: prior.oldQty }),
    ).filter((q) => q.oldQty !== q.newQty);

    if (quantitiesChanged.length === 0) return null;
    return withIdentity({ ...last, quantitiesChanged }, incoming);
}

function mergeAddItemNote(
    last: AuditLogAddNoteAction,
    incoming: AuditLogAddNoteAction,
): AuditLogAddNoteAction | null {
    const itemNoteChanges = mergeArrays(
        last.itemNoteChanges,
        incoming.itemNoteChanges,
        (n) => n.variantId,
        (prior, fresh) => ({ ...fresh, previousNote: prior.previousNote }),
    ).filter((n) => n.previousNote !== n.currentNote);

    if (itemNoteChanges.length === 0) return null;
    return withIdentity({ ...last, itemNoteChanges }, incoming);
}

function mergeModifierChanged(
    last: AuditLogUpdateModifiersAction,
    incoming: AuditLogUpdateModifiersAction,
): AuditLogUpdateModifiersAction | null {
    const modifiersChanged = mergeArrays(
        last.modifiersChanged,
        incoming.modifiersChanged,
        (entry) => entry.cartItemId,
        (prior, fresh) => ({
            ...fresh,
            modifiers: mergeArrays(
                prior.modifiers,
                fresh.modifiers,
                (modifier) => modifier.modifierOptionId,
                (priorModifier, freshModifier) => ({
                    ...freshModifier,
                    oldQty: priorModifier.oldQty,
                }),
            ).filter((modifier) => modifier.oldQty !== modifier.newQty),
        }),
    ).filter((entry) => entry.modifiers.length > 0);

    if (modifiersChanged.length === 0) return null;
    return withIdentity({ ...last, modifiersChanged }, incoming);
}

function mergeEdited(
    last: AuditLogEditedAction,
    incoming: AuditLogEditedAction,
): AuditLogEditedAction | null {
    const changedFields = mergeArrays(
        last.changedFields,
        incoming.changedFields,
        (cf) => cf.field,
        (prior, fresh) => ({ ...fresh, previousValue: prior.previousValue }),
    ).filter((cf) => cf.previousValue !== cf.currentValue);

    if (changedFields.length === 0) return null;
    return withIdentity({ ...last, changedFields }, incoming);
}

function mergeUpdated(
    last: AuditLog,
    incoming: AuditLog & { action: typeof AUDIT_LOG_ACTION.UPDATED },
): AuditLog | null {
    if (incoming.cartUpdateType === AUDIT_LOG_CART_UPDATE_TYPE.ADD_ITEMS) {
        return mergeAddItems(last as AuditLogAddItemsAction, incoming);
    }
    if (incoming.cartUpdateType === AUDIT_LOG_CART_UPDATE_TYPE.REMOVE_ITEMS) {
        return mergeRemoveItems(last as AuditLogRemoveItemsAction, incoming);
    }
    if (
        incoming.cartUpdateType === AUDIT_LOG_CART_UPDATE_TYPE.UPDATE_QUANTITIES
    ) {
        return mergeUpdateQuantities(
            last as AuditLogUpdateQuantitiesAction,
            incoming,
        );
    }
    if (incoming.cartUpdateType === AUDIT_LOG_CART_UPDATE_TYPE.ADD_ITEM_NOTE) {
        return mergeAddItemNote(last as AuditLogAddNoteAction, incoming);
    }
    if (
        incoming.cartUpdateType === AUDIT_LOG_CART_UPDATE_TYPE.MODIFIER_CHANGED
    ) {
        return mergeModifierChanged(
            last as AuditLogUpdateModifiersAction,
            incoming,
        );
    }
    return incoming;
}

function mergeLogs(last: AuditLog, incoming: AuditLog): AuditLog | null {
    if (
        last.action === AUDIT_LOG_ACTION.UPDATED &&
        incoming.action === AUDIT_LOG_ACTION.UPDATED &&
        last.cartUpdateType === incoming.cartUpdateType
    ) {
        return mergeUpdated(last, incoming);
    }

    if (
        last.action === AUDIT_LOG_ACTION.EDITED &&
        incoming.action === AUDIT_LOG_ACTION.EDITED
    ) {
        return mergeEdited(last, incoming);
    }

    return incoming;
}

export function compressAuditLogs(
    existing: AuditLog[],
    incoming: AuditLog,
): AuditLog[] {
    if (existing.length === 0) return [incoming];

    if (!isMergeable(incoming)) return [...existing, incoming];

    const last = existing[existing.length - 1];
    if (!isMergeable(last)) return [...existing, incoming];

    if (getBucketKey(last) !== getBucketKey(incoming)) {
        return [...existing, incoming];
    }

    const merged = mergeLogs(last, incoming);
    if (merged === null) return existing.slice(0, -1);

    return [...existing.slice(0, -1), merged];
}
